import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Countdown from 'react-countdown';
import i18n from '../../../../../i18n';
import { MapPin, Gavel, CalendarClock, CalendarCheck, Eye, Tag } from 'lucide-react';
import { OmrRiyal } from '../../../../common/icons';
import { BiddingModal } from '../../../../helpers/biddingModal';

const lng = localStorage.getItem('I18N_LANGUAGE');
const is_logged = localStorage.getItem('is_logged_in') ? true : false;

class AuctionListCard extends Component {
    constructor(props) {
        super(props);
        this.state = { completed: false, open_popup: false, bid_count: 0 };
    }

    componentDidMount() {
        if (this.props.auction) this.setState({ bid_count: this.props.auction.bid_count || 0 });
    }

    createDate = (dt) => (dt ? new Date(dt.replace(/-/g, '/')) : null);
    getNow = () => {
        const time = new Date().toLocaleTimeString('en-US', { timeZone: 'Asia/Muscat' });
        const dt = new Date().toISOString().split('T')[0];
        return new Date((dt + ' ' + time).replace(/-/g, '/'));
    };
    openBidModal = (e) => { e.preventDefault(); e.stopPropagation(); this.setState({ open_popup: true }); };
    closeBidModal = () => { this.setState({ open_popup: false }); };
    updateRow = (data) => { this.setState({ bid_count: data }); };

    renderer = ({ days, hours, minutes, seconds, completed }) => {
        if (completed) return <span className="mzlc-timer__done">{i18n.t('ended') || 'Ended'}</span>;
        const pad = (n) => String(n).padStart(2, '0').split('');
        const units = [
            { val: days, label: i18n.t('lbl_day') || 'Day', k: 'd' },
            { val: hours, label: i18n.t('lbl_hour') || 'Hr', k: 'h' },
            { val: minutes, label: i18n.t('lbl_minute') || 'Min', k: 'm' },
            { val: seconds, label: i18n.t('lbl_second') || 'Sec', k: 's' },
        ];
        return (
            <div className="mzlc-timer">
                {units.map((u) => (
                    <div className="mzlc-timer__unit" key={u.k}>
                        <div className="mzlc-timer__digits">
                            {pad(u.val).map((d, i) => <span key={`${u.k}${i}`} className="mzlc-timer__digit">{d}</span>)}
                        </div>
                        <span className="mzlc-timer__label">{u.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    render() {
        const { auction } = this.props;
        if (!auction) return null;

        const images = auction.images || [];
        const imgSrc = images.length > 0 ? images[0].image : process.env.PUBLIC_URL + '/assets/images/placeholder.jpg';

        const isActive = auction.status === 'A' || auction.status_dis === 'A';
        const isUpcoming = auction.status === 'U' || auction.status_dis === 'U';
        const isExpired = auction.status === 'E' || auction.status_dis === 'E';

        const title = lng === 'ar' ? (auction.title_ar || auction.title) : auction.title;
        const auctionType = auction.auction_type_label || auction.auction_type || '';
        const auctionDays = auction.auction_days || '';
        const cities = lng === 'ar' ? (auction.location_ar || auction.location) : auction.location;
        const startDate = auction.start_date_formatted || auction.start_date || '';
        const endDate = auction.end_date_formatted || auction.end_date || '';

        let countdownDate = null;
        let countdownLabel = '';
        if (isUpcoming && auction.start_date) {
            countdownDate = this.createDate(auction.start_date);
            countdownLabel = i18n.t('starts_after');
        } else if (isActive && auction.end_date) {
            countdownDate = this.createDate(auction.end_date);
            countdownLabel = i18n.t('end_in');
        }

        return (
            <>
                <div className="mzlc">
                    <Link to={`/auction-details/${auction.id}`} className="mzlc__link">

                        {/* ── COL 1: Image ── */}
                        <div className="mzlc__img">
                            <img src={imgSrc} alt={title} />
                            <div className="mzlc__badges">
                                {isActive && <span className="mzlc__badge mzlc__badge--active">{i18n.t('active')}</span>}
                                {isUpcoming && <span className="mzlc__badge mzlc__badge--upcoming">{i18n.t('upcoming')}</span>}
                                {isExpired && <span className="mzlc__badge mzlc__badge--expired">{i18n.t('expired')}</span>}
                                {auctionType && <span className="mzlc__badge mzlc__badge--type">{auctionType}</span>}
                            </div>
                        </div>

                        {/* ── COL 2: Info + Timer ── */}
                        <div className="mzlc__info">
                            <span className="mzlc__id">MZAD{auction.id}</span>
                            <h4 className="mzlc__title">{title}</h4>

                            {cities && (
                                <div className="mzlc__location">
                                    <MapPin size={13} />
                                    <span>{cities}</span>
                                </div>
                            )}

                            {auctionDays && (
                                <div className="mzlc__days">
                                    <Tag size={12} />
                                    <span>{i18n.t('auction_days') || 'Auction days'}: {auctionDays}</span>
                                </div>
                            )}

                            {countdownDate && (
                                <div className="mzlc__countdown">
                                    <span className="mzlc__countdown-label">{countdownLabel}</span>
                                    <Countdown
                                        date={new Date(countdownDate).getTime()}
                                        renderer={this.renderer}
                                        now={this.getNow}
                                        onComplete={() => this.setState({ completed: true })}
                                    />
                                </div>
                            )}
                            {isExpired && !countdownDate && (
                                <span className="mzlc-timer__done">{i18n.t('auction_ended')}</span>
                            )}
                        </div>

                        {/* ── COL 3: Amounts ── */}
                        <div className="mzlc__amounts">
                            <div className="mzlc__amount mzlc__amount--primary">
                                <span className="mzlc__amount-label">{i18n.t('current_amount')}</span>
                                <span className="mzlc__amount-value">
                                    {auction.current_amount}
                                    <OmrRiyal height={14} width={14} />
                                </span>
                            </div>
                            <div className="mzlc__amount">
                                <span className="mzlc__amount-label">{i18n.t('start_amount')}</span>
                                <span className="mzlc__amount-value">
                                    {auction.start_amount}
                                    <OmrRiyal height={14} width={14} />
                                </span>
                            </div>
                        </div>

                        {/* ── COL 4: Dates ── */}
                        <div className="mzlc__dates">
                            <div className="mzlc__date-row">
                                <CalendarClock size={14} className="mzlc__date-icon" />
                                <div>
                                    <div className="mzlc__date-label">{i18n.t('start_date')}</div>
                                    <div className="mzlc__date-value">{startDate}</div>
                                </div>
                            </div>
                            <div className="mzlc__date-row">
                                <CalendarCheck size={14} className="mzlc__date-icon" />
                                <div>
                                    <div className="mzlc__date-label">{i18n.t('end_date')}</div>
                                    <div className="mzlc__date-value">{endDate}</div>
                                </div>
                            </div>
                        </div>

                        {/* ── COL 5: Actions ── */}
                        <div className="mzlc__actions">
                            {auction?.my_rank && (
                                <span className="mzlc__rank-badge">
                                    <span className="mzlc__rank-badge__label">{i18n.t('my_rank')}</span>
                                    <span className="mzlc__rank-badge__value"># {auction.my_rank}</span>
                                </span>
                            )}
                            <Link to={'/auction-details/' + auction.id} className="mzlc__btn mzlc__btn--details">
                                <Eye size={14} />
                                <span>{i18n.t('view_details') || 'Details'}</span>
                            </Link>

                            {auction.status === 'A' && !isExpired && auction.is_enrolled && (
                                <button onClick={this.openBidModal} className="mzlc__btn mzlc__btn--bid">
                                    <Gavel size={14} />
                                    <span>{i18n.t('bid_now') || 'Bid Now'}</span>
                                </button>
                            )}

                            {auction.status === 'A' && !auction.is_enrolled && (
                                <Link to={!is_logged ? '/login' : '/enroll/' + auction.id} className="mzlc__btn mzlc__btn--enroll">
                                    {i18n.t('enroll') || 'Enroll'}
                                </Link>
                            )}
                        </div>
                    </Link>
                </div>

                <BiddingModal
                    open_popup={this.state.open_popup}
                    closeModal={this.closeBidModal}
                    auction={auction}
                    lng={lng}
                    updateRow={this.updateRow}
                />
            </>
        );
    }
}

export default AuctionListCard;